"use client";

import { DocsLayout } from "fumadocs-ui/layouts/docs";
import { RootProvider } from "fumadocs-ui/provider/next";
import { source } from "@/lib/docs-source";
import { BookOpen, History, Home } from "lucide-react";
import type { ReactNode } from "react";
import { DocsThemeSwitcher } from "./DocsThemeSwitcher";
import { FakiDocs } from "./FakiDocs";
import { DocsFakiProvider, useDocsFaki } from "./DocsFakiContext";
import { DocsHashHandler } from "./DocsHashHandler";
import { useIsDesktop } from "@/lib/copilot/useIsDesktop";
import { OrkestriaLogoMark } from "@/components/brand/OrkestriaLogoMark";
import "fumadocs-ui/style.css";
import "@/app/docs/docs.css";

export function DocsShell({ children }: { children: ReactNode }) {
  return (
    <RootProvider theme={{ enabled: false }}>
      <DocsFakiProvider>
        <DocsShellInner>{children}</DocsShellInner>
        <FakiDocs />
        <DocsHashHandler />
      </DocsFakiProvider>
    </RootProvider>
  );
}

/**
 * Wrapper interno: cuando Faki está abierto en modo "immersed" (solo desktop)
 * aplica `padding-right: width` con transición suave → el contenido se
 * "empuja" hacia la izquierda en vez de quedar tapado.
 */
function DocsShellInner({ children }: { children: ReactNode }) {
  const { open, mode, width } = useDocsFaki();
  const isDesktop = useIsDesktop();
  const pushed = open && mode === "immersed" && isDesktop;

  return (
    <div
      style={{
        paddingRight: pushed ? width : 0,
        transition: "padding-right 280ms cubic-bezier(0.32, 0.72, 0, 1)",
      }}
    >
      <DocsLayout
        tree={source.pageTree}
        themeSwitch={{ enabled: false }}
        nav={{
          title: (
            <span className="inline-flex items-center gap-2">
              <OrkestriaLogoMark size={26} />
              <span className="font-semibold tracking-tight">Orkestria Docs</span>
            </span>
          ),
        }}
        tabs={[
          {
            title: "Inicio",
            description: "Visión general",
            url: "/docs",
            icon: <Home className="h-4 w-4" />,
          },
          {
            title: "Changelogs",
            description: "Notas de versión",
            url: "/docs/changelogs",
            icon: <History className="h-4 w-4" />,
          },
          {
            title: "Manual",
            description: "Guía de usuario",
            url: "/docs/manual",
            icon: <BookOpen className="h-4 w-4" />,
          },
        ]}
        links={[
          {
            type: "custom",
            children: <DocsThemeSwitcher />,
            secondary: true,
          },
          { text: "← Dashboard", url: "/dashboard" },
        ]}
      >
        {children}
      </DocsLayout>
    </div>
  );
}
